import styled, { css } from 'styled-components';
import { darken } from 'polished'; // Biblioteca útil para manipulação de cores

import { hexToRgba } from '~/common/utils/hex-to-rgba';
import { SPACING } from '~/common/layout-propertis/spacing';
import { RADIUS } from '~/common/layout-propertis/radius';

export const ArrowContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

interface IButtonContainerProps {
  width: string;
  backgroundColor: string;
  textColor: string;
  textOrIconColorOnHover: string;
  borderColor: string;
  disabled: boolean;
  withoutHoverEffect: boolean;
  customCssBorderHover: string;
  justifyContent: string;
  height: string;
  fancy: boolean;
  circular: boolean;
}

export const ButtonContainer = styled.button<IButtonContainerProps>`
  ${({ fancy, backgroundColor }) =>
    fancy
      ? css`
          background: linear-gradient(
              180deg,
              rgba(255, 255, 255, 0.16) 0%,
              rgba(255, 255, 255, 0) 100%
            ),
            ${backgroundColor};
        `
      : css`
          background-color: ${backgroundColor};
        `}

  width: ${({ width }) => width};
  height: ${({ height }) => height};
  max-height: ${({ height }) => height} !important;
  border: 1px solid ${({ borderColor }) => borderColor};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'all')};
  cursor: pointer;
  padding: 10px;
  overflow: hidden;
  border-radius: ${({ circular }) => (circular ? '999px' : RADIUS._4)};
  gap: ${SPACING._4};
  display: flex;
  flex-direction: row;
  justify-content: ${({ justifyContent }) => justifyContent};
  align-items: center;
  gap: ${SPACING._12};
  user-select: none;
  transition: box-shadow 200ms ease-in-out, outline 200ms ease-in-out,
    border 200ms ease-in-out, background-color 200ms ease-in-out;

  outline: 1px ${({ backgroundColor }) => hexToRgba(backgroundColor, 0.5)} solid;
  outline-offset: -1px;
  position: relative; /* Necessário para os pseudo-elementos */

  div,
  span,
  svg {
    position: relative;
    z-index: 2; /* Mantém o conteúdo acima de todas as camadas */
  }

  #arrow {
    img {
      transition: 200ms;
    }
  }

  span {
    white-space: nowrap;
    transition: 200ms;
    color: ${({ textColor }) => textColor};
  }

  svg {
    transition: 200ms;
  }

  ${({
    backgroundColor,
    textOrIconColorOnHover,
    withoutHoverEffect,
    customCssBorderHover,
  }) =>
    withoutHoverEffect
      ? css`
          &:hover {
            border-color: black;
            box-shadow: 0px 0px 0px 4px rgba(153, 160, 174, 0.16);
          }
        `
      : css`
          /* Gradiente de hover */
          &::before {
            content: '';
            position: absolute;
            inset: 0;
            background: linear-gradient(
              -135deg,
              rgba(0, 0, 0, 1) 0%,
              rgba(0, 0, 0, 0) 50%,
              rgba(0, 0, 0, 1) 100%
            );
            opacity: 0;
            transition: ${!customCssBorderHover && 'opacity 200ms ease-in-out'};
            z-index: 0; /* Mantém o gradiente no fundo */
          }

          /* Camada escura que usa a cor escurecida do backgroundColor */
          &::after {
            content: '';
            position: absolute;
            inset: 1px; /* Cria o espaçamento de 1px entre a camada e o conteúdo */
            background: ${darken(0.25, backgroundColor)}; /* Escurece a cor */
            opacity: 0;
            transition: ${!customCssBorderHover && 'opacity 200ms ease-in-out'};
            z-index: 1; /* Agora está acima do gradiente e abaixo do conteúdo */
            border-radius: calc(
              ${RADIUS._4} - 1px
            ); /* Mantém a borda arredondada respeitando o padding */
          }

          /* Efeito de hover */
          &:hover {
            ${customCssBorderHover ||
            `
            border-color: black;
            box-shadow: 0px 0px 0px 4px rgba(153, 160, 174, 0.16);
            `}

            span {
              color: ${textOrIconColorOnHover};
            }

            &::before {
              opacity: 1; /* Ativa o gradiente */
            }

            &::after {
              opacity: 1; /* Aplica a camada escura (agora com cor sólida mais escura) */
            }
          }
        `}

  ${({ disabled, backgroundColor }) =>
    disabled &&
    css`
      background-color: ${hexToRgba(backgroundColor, 0.25)};
    `}
`;

export const LoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
`;
